import React from 'react'
import {Typography} from '@mui/material'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'

export interface MedicalCenterRow {
  _id: string
  type?: string
  name?: string
  phoneNumber?: string
  faxNumber?: string
  email?: string
  activeDoctorsCount?: number
  code?: string
  country?: string
  region?: string
  province?: string
  city?: string
  address?: string
  streetNumber?: string
  postalCode?: string
  locality?: string
  [key: string]: any
}

interface MedicalCenterColumnConfig {
  id: string
  label: string
}

// Same mapping as the legacy page's parseType (src/pages/medicalCenters/MedicalCenters.tsx)
// — kept here (rather than PSP-driven) since the backend's type enum is fixed and both
// stacks must show identical Italian labels.
export function getMedicalCenterTypeLabel(type: string | undefined): string {
  switch (type) {
    case 'HOSPITAL':
      return 'Ospedale'
    case 'ASL':
      return 'ASL'
    case 'OTHER':
      return 'Altro'
    default:
      return '-'
  }
}

// Same id/label pairs as the legacy DEFAULT_MEDICAL_CENTER_COLUMNS.
const DEFAULT_COLUMNS: MedicalCenterColumnConfig[] = [
  {id: 'type', label: 'TIPO'},
  {id: 'name', label: 'NOME'},
  {id: 'phoneNumber', label: 'TELEFONO'},
  {id: 'faxNumber', label: 'FAX'},
  {id: 'email', label: 'EMAIL'},
  {id: 'activeDoctorsCount', label: 'MEDICI ATTIVI'},
]

function renderCell(id: string) {
  return (row: MedicalCenterRow): React.ReactNode => {
    if (id === 'type') {
      return <StatusPill label={getMedicalCenterTypeLabel(row.type)} color="default" />
    }
    const value = (row as any)[id]
    return <Typography variant="body2">{value ?? '—'}</Typography>
  }
}

/**
 * Columns are PSP-driven (`medicalCentersConfigurations.columnOptions`), falling back to
 * a sensible default set — mirrors the pattern in biologicalSamplesColumns.tsx. `name`
 * renders as the mobile card header and `type` as its top-right badge.
 */
export function buildColumns(columnConfig?: MedicalCenterColumnConfig[]): ColumnDef<MedicalCenterRow>[] {
  const config = columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
  return config.map(col => ({
    key: col.id,
    header: col.label,
    mobileCardHeader: col.id === 'name',
    mobileCardBadge: col.id === 'type',
    render: renderCell(col.id),
  }))
}
